import React, { memo, useEffect, useRef, useState } from 'react'
import { Checkbox, Tooltip } from 'antd'
import { KeywordCheckboxProps } from '../types'

/**
 * KeywordCheckbox component with smart tooltip
 * Shows tooltip only when text is truncated with ellipsis
 *
 * Features:
 * - Automatic overflow detection
 * - Smart tooltip display (only when needed)
 * - Responsive to window resize events
 * - Reusable across parent components
 */
export const KeywordCheckbox = memo(({ value, checked, onChange, className }: KeywordCheckboxProps) => {
    const textRef = useRef<HTMLSpanElement>(null)
    const [showTooltip, setShowTooltip] = useState(false)

    useEffect(() => {
        const checkOverflow = () => {
            if (textRef.current) {
                const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth
                setShowTooltip(isOverflowing)
            }
        }

        checkOverflow()

        // Check on resize in case the container size changes
        window.addEventListener('resize', checkOverflow)
        return () => window.removeEventListener('resize', checkOverflow)
    }, [value])

    const checkboxContent = (
        <span ref={textRef} style={{ display: 'block' }}>
            {value}
        </span>
    )

    return (
        <Checkbox value={value} checked={checked} onChange={(e) => onChange(e.target.checked)} className={className}>
            {showTooltip ? (
                <Tooltip title={value} placement="top">
                    {checkboxContent}
                </Tooltip>
            ) : (
                checkboxContent
            )}
        </Checkbox>
    )
})

KeywordCheckbox.displayName = 'KeywordCheckbox'
